import React, {useEffect, useMemo, useState, useRef} from 'react';
import './Search.css';

export default function Search() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateQuery, setStateQuery] = useState('');
  const [districtQuery, setDistrictQuery] = useState('');
  const [officenameQuery, setOfficenameQuery] = useState('');
  const [officenameAlpha, setOfficenameAlpha] = useState(''); // first alphabet filter for officename
  const [officenameSelected, setOfficenameSelected] = useState(''); // selected officename from dropdown
  const [selectedState, setSelectedState] = useState('');
  const mounted = useRef(true);

  function formatKey(key) {
    const map = {
      circlename: 'Circle Name',
      regionname: 'Region Name',
      divisionname: 'Division Name',
      officename: 'Office Name',
      pincode: 'PIN Code',
      officetype: 'Office Type',
      delivery: 'Delivery',
      district: 'District',
      statename: 'State Name',
      latitude: 'Latitude',
      longitude: 'Longitude'
    };
    if (map[key]) return map[key];
    // split camelCase/snake_case/concatenated lower-case into words
    let s = key.replace(/_/g, ' ')
               .replace(/([a-z])([A-Z])/g, '$1 $2')
               .replace(/([a-zA-Z])([0-9])/g, '$1 $2');
    // if still single lowercase token, try to insert spaces before common words
    s = s.replace(/(name|code|type|status|city|village|office|pin|pincode)/gi, match => ' ' + match);
    // normalize spaces
    s = s.replace(/\s+/g, ' ').trim();
    // title case
    return s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  // load full dataset from public/ on mount (fetch avoids bundling large file)
  useEffect(() => {
    mounted.current = true;
    setLoading(true);
    fetch('/data/pincodes.json')
      .then(res => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.json();
      })
      .then(arr => {
        if (!mounted.current) return;
        setData(Array.isArray(arr) ? arr : []);
      })
      .catch(err => { console.error('Failed to fetch /data/pincodes.json', err); setData([]); })
      .finally(() => { if (mounted.current) setLoading(false); });
    return () => { mounted.current = false; };
  }, []);

  const states = useMemo(() => {
    const s = new Set();
    data.forEach(d => d.statename && s.add(d.statename));
    return Array.from(s).sort();
  }, [data]);

  const districtsForState = useMemo(() => {
    if (!selectedState) return [];
    const s = new Set();
    data.filter(d => d.statename === selectedState && d.district).forEach(d => s.add(d.district));
    return Array.from(s).sort();
  }, [selectedState, data]);

  const results = useMemo(() => {
    if (!data || data.length === 0) return [];
    const sq = stateQuery.trim().toLowerCase();
    const dq = districtQuery.trim().toLowerCase();
    const oq = officenameQuery.trim().toLowerCase();

    return data.filter(d => {
      if (selectedState && d.statename !== selectedState) return false;
      if (sq && (!d.statename || !d.statename.toLowerCase().includes(sq))) return false;
      if (dq && (!d.district || !d.district.toLowerCase().includes(dq))) return false;
      if (oq && (!d.officename || !d.officename.toLowerCase().includes(oq))) return false;
      // pincode search removed
      return true;
    });
  }, [data, selectedState, districtQuery, stateQuery, officenameQuery]);

  // compute officenames that start with selected alphabet, optionally filtered by state/district
  const officenamesForAlpha = useMemo(() => {
    if (!officenameAlpha) return [];
    const s = new Set();
    data.forEach(d => {
      if (!d.officename) return;
      if (selectedState && d.statename !== selectedState) return;
      if (districtQuery && d.district && districtQuery !== '' && d.district !== districtQuery) return;
      const name = String(d.officename).trim();
      if (!name) return;
      if (name.charAt(0).toUpperCase() === officenameAlpha.toUpperCase()) s.add(name);
    });
    return Array.from(s).sort();
  }, [data, officenameAlpha, selectedState, districtQuery]);

  // clear alphabet and selected officename when user changes state or district
  useEffect(() => {
    setOfficenameAlpha('');
    setOfficenameSelected('');
    setOfficenameQuery('');
  }, [selectedState, districtQuery]);

  useEffect(() => {
    if (stateQuery && states.length === 1) setSelectedState(states[0]);
  }, [stateQuery, states]);

  const MAX_RESULTS = 200;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    setPage(1);
  }, [results, selectedState, districtQuery, stateQuery, officenameQuery]);

  const totalPages = Math.max(1, Math.ceil(results.length / pageSize));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages]);

  useEffect(() => {
    let title = 'Indian PIN Code Search';
    if (officenameQuery) {
      title = `${officenameQuery} Post Office - Indian PIN Code Search`;
    } else if (districtQuery && selectedState) {
      title = `PIN Codes in ${districtQuery}, ${selectedState}`;
    } else if (selectedState) {
      title = `PIN Codes in ${selectedState} - Indian PIN Code Search`;
    }
    
    if (title.length > 60) {
        if (officenameQuery) {
            title = `${officenameQuery} Post Office`;
        } else if (districtQuery && selectedState) {
            title = `PIN Codes in ${districtQuery}`;
        } else if (selectedState) {
            title = `PIN Codes in ${selectedState}`;
        }
    }

    document.title = title;
  }, [officenameQuery, districtQuery, selectedState]);

  const shownResults = results.slice((page - 1) * pageSize, page * pageSize);

  // modal state for map preview
  const [mapOpen, setMapOpen] = useState(false);
  const [mapCoords, setMapCoords] = useState({lat: null, lng: null, title: ''});
  const mapContainerRef = useRef(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && mapOpen) setMapOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mapOpen]);

  function openMap(r) {
    const lat = r.latitude || r.lat || null;
    const lng = r.longitude || r.lon || r.lng || null;
    if (!lat || !lng) return;
    setMapCoords({lat, lng, title: r.officename || r.officename});
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (key && typeof window !== 'undefined') {
      // try to load Google Maps JS API dynamically
      if (window.google && window.google.maps) {
        setGoogleMapsLoaded(true);
        setMapOpen(true);
      } else {
        const src = `https://maps.googleapis.com/maps/api/js?key=${key}`;
        // avoid adding multiple script tags
        const existing = Array.from(document.getElementsByTagName('script')).find(s => s.src && s.src.startsWith('https://maps.googleapis.com/maps/api/js'));
        if (existing) {
          existing.addEventListener('load', () => { setGoogleMapsLoaded(true); setMapOpen(true); });
          existing.addEventListener('error', () => { setMapOpen(true); });
        } else {
          const s = document.createElement('script');
          s.src = src;
          s.async = true;
          s.defer = true;
          s.onload = () => { setGoogleMapsLoaded(true); setMapOpen(true); };
          s.onerror = () => { console.warn('Google Maps JS API failed to load, falling back to iframe'); setMapOpen(true); };
          document.head.appendChild(s);
        }
      }
    } else {
      setMapOpen(true);
    }
  }

  useEffect(() => {
    if (!mapOpen) return;
    if (!(window && window.google && window.google.maps && mapContainerRef.current)) return;
    const lat = Number(mapCoords.lat);
    const lng = Number(mapCoords.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    // initialize map
    try {
      const map = new window.google.maps.Map(mapContainerRef.current, {
        center: {lat, lng},
        zoom: 15,
      });
      new window.google.maps.Marker({position: {lat, lng}, map, title: mapCoords.title});
    } catch (e) {
      console.error('Failed to init Google map', e);
    }
  }, [mapOpen, googleMapsLoaded, mapCoords]);

  return (
    <section className="search-wrap">
      <div className="search-controls">
        <div className="control col-state">
          <label>State</label>
          <select
            value={selectedState}
            onChange={e => setSelectedState(e.target.value)}
          >
            <option value="">— All States —</option>
            {states.map(s => (<option key={s} value={s}>{s}</option>))}
          </select>
        </div>

        <div className="control col-district">
          <label>District</label>
          <select
            value={districtQuery}
            onChange={e => setDistrictQuery(e.target.value)}
            disabled={!selectedState}
          >
            <option value="">— All Districts —</option>
            {districtsForState.map(d => (<option key={d} value={d}>{d}</option>))}
          </select>
        </div>

        {/* Pincode search removed */}

        <div className="control wide">
          <label>Post Office name</label>
          <div style={{display:'flex',gap:8}}>
            <select
              value={officenameAlpha}
              onChange={e => { setOfficenameAlpha(e.target.value); setOfficenameSelected(''); setOfficenameQuery(''); }}
            >
              <option value="">— Post Office Name 1st Alphabet —</option>
              {Array.from({length:26}).map((_,i) => {
                const ch = String.fromCharCode(65 + i);
                return <option key={ch} value={ch}>{ch}</option>;
              })}
            </select>

            <select
              value={officenameSelected}
              onChange={e => { setOfficenameSelected(e.target.value); setOfficenameQuery(e.target.value); }}
              disabled={!officenameAlpha}
              style={{flex:1}}
            >
              <option value="">— All Post Offices —</option>
              {officenamesForAlpha.map(n => (<option key={n} value={n}>{n}</option>))}
            </select>
          </div>
        </div>
      </div>

      <div className="results">
        {loading ? (
          <div className="results-count">Loading dataset…</div>
        ) : (
          <>
            <div className="results-header">
              <div className="results-count">{results.length} match(es) — page {page} / {totalPages}</div>
              <div className="view-toggle">
                <button
                  className={viewMode==='grid' ? 'active' : ''}
                  title="Grid view"
                  onClick={() => setViewMode('grid')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="8" height="8" stroke="#334155" strokeWidth="1.2" fill="none"/><rect x="13" y="3" width="8" height="8" stroke="#334155" strokeWidth="1.2" fill="none"/><rect x="3" y="13" width="8" height="8" stroke="#334155" strokeWidth="1.2" fill="none"/><rect x="13" y="13" width="8" height="8" stroke="#334155" strokeWidth="1.2" fill="none"/></svg>
                </button>
                <button
                  className={viewMode==='list' ? 'active' : ''}
                  title="List view"
                  onClick={() => setViewMode('list')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="5" width="18" height="3" stroke="#334155" strokeWidth="1.2" fill="none"/><rect x="3" y="10.5" width="18" height="3" stroke="#334155" strokeWidth="1.2" fill="none"/><rect x="3" y="16" width="18" height="3" stroke="#334155" strokeWidth="1.2" fill="none"/></svg>
                </button>
              </div>
            </div>
            <div className="pagination">
              <div className="page-controls">
                <button onClick={() => setPage(1)} disabled={page===1}>« First</button>
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}>‹ Prev</button>
                <span className="page-info">Page</span>
                <input type="number" min={1} max={totalPages} value={page} onChange={e => setPage(Number(e.target.value) || 1)} />
                <span className="page-info">of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages}>Next ›</button>
                <button onClick={() => setPage(totalPages)} disabled={page===totalPages}>Last »</button>
              </div>
              <div className="page-size">
                <label>Per page</label>
                <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                  {[10,20,50,100].map(n => (<option key={n} value={n}>{n}</option>))}
                </select>
              </div>
            </div>

            <ul className={viewMode === 'list' ? 'list' : 'grid'}>
              {shownResults.map((r, i) => (
                <li key={i} className={"result-item " + viewMode}>
                  <div className="result-row">
                    <strong>{r.officename}</strong>
                    <div style={{display:'flex', gap:8, alignItems:'center'}}>
                      <span className="pincode">{r.pincode}</span>
                      <button
                        className="map-btn"
                        onClick={() => openMap(r)}
                        disabled={!(r.latitude && r.longitude)}
                        title={r.latitude && r.longitude ? 'Open map' : 'No coordinates available'}
                      >
                        Map
                      </button>
                    </div>
                  </div>
                  <div className="detail-grid">
                    {Object.keys(r)
                      .filter(k => k !== 'latitude' && k !== 'longitude')
                      .map(k => (
                        <React.Fragment key={k}>
                          <div className="col-name">{formatKey(k)}</div>
                          <div className="col-value">{r[k] === null || r[k] === undefined ? '—' : String(r[k])}</div>
                        </React.Fragment>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
            {mapOpen && (
              <div className="map-modal" onClick={() => setMapOpen(false)}>
                <div className="map-modal-dialog" onClick={e => e.stopPropagation()}>
                  <div className="map-modal-header">
                    <div>{mapCoords.title}</div>
                    <button className="map-close" onClick={() => setMapOpen(false)}>✕</button>
                  </div>
                  <div className="map-modal-body">
                    {process.env.REACT_APP_GOOGLE_MAPS_API_KEY && googleMapsLoaded ? (
                      <div ref={mapContainerRef} className="map-canvas" />
                    ) : (
                      <iframe
                        title="map"
                        src={`https://www.google.com/maps?q=${mapCoords.lat},${mapCoords.lng}&z=15&output=embed`}
                        width="100%"
                        height="400"
                        style={{border:0}}
                        allowFullScreen
                        loading="lazy"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
